import axiosInstance from "./api";
const cookies = require("js-cookie");
import config from "../config/index";
import { withReturnParam } from "../util/returnPath";

// Auth cookies are written with { domain: config.cookiedomain } (see Login.vue), so they
// can only be deleted with the same domain + path. Without them js-cookie silently leaves
// the dead cookies in place and every request keeps looping through the 401/refresh path.
const clearSessionAndRedirect = () => {
  const cookieOptions = { domain: config.cookiedomain, path: "/" };
  cookies.remove("fmw_refresh_token", cookieOptions);
  cookies.remove("fmw_access_token", cookieOptions);
  cookies.remove("user", cookieOptions);

  // Admin configs have no {{domain}} placeholder; the replace is a defensive no-op that
  // keeps parity with the user-frontend and survives a config that does use one.
  const subdomain = location.hostname.split(".", 1)[0];
  const loginUrl = config.url.main.replace("{{domain}}", subdomain) + "/inloggen";
  // A session dying mid-Deeplink is the same loss of context as arriving logged
  // out, so carry the Terugkeerpad here too (docs/adr/0028).
  location.replace(withReturnParam(loginUrl, location.pathname, location.search));
};

// Single-flight refresh: the Laravel backend rotates the refresh token per session, so
// concurrent 401s must share ONE /v1/auth/token call. Otherwise the first refresh wins and
// the rest POST an already-rotated token, get { success: false } and nuke the freshly-issued
// session. Sessions are per device since ADR-0017, so losing that race costs this tab its
// session and nothing else — logging in elsewhere no longer ends this one.
// See Fitte-Medewerker-Api/docs/adr/0017-concurrent-sessions-with-per-session-rotation.md
let refreshPromise = null;
const refreshAuthToken = () => {
  if (!refreshPromise) {
    refreshPromise = axiosInstance
      .post("/v1/auth/token", {
        refresh_token: cookies.get("fmw_refresh_token"),
      })
      .finally(() => {
        refreshPromise = null;
      });
  }
  return refreshPromise;
};

const setup = () => {
  axiosInstance.interceptors.request.use(
    (config) => {
      let token = cookies.get("fmw_access_token");
      if (token) {
        config.headers["Authorization"] = "bearer " + token; // for Spring Boot back-end
      }
      return config;
    },
    (error) => {
      return Promise.reject(error);
    }
  );

  axiosInstance.interceptors.response.use(
    (res) => {
      return res;
    },
    async (err) => {
      const originalConfig = err.config;
      if (originalConfig.url !== "/v1/auth/step" && err.response) {
        // Access Token was expired
        if (err.response.status === 401 && !originalConfig._retry) {
          originalConfig._retry = true;

          try {
            const res = await refreshAuthToken();
            if (res.data.success) {
              cookies.set("fmw_refresh_token", res.data.data.refresh_token, {
                domain: config.cookiedomain,
              });
              cookies.set("fmw_access_token", res.data.data.access_token, {
                domain: config.cookiedomain,
              });
            } else {
              clearSessionAndRedirect();
              return Promise.reject();
            }

            return axiosInstance(originalConfig);
          } catch (_error) {
            clearSessionAndRedirect();
            return Promise.reject(_error);
          }
        }
      }
      return Promise.reject(err);
    }
  );
};

export default setup;
